import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';
import {NewReport, PowerBiToken, Report, ReportStatus} from '../_types/reports';
import {GenericResponse, GenericResponseWithPagination} from '../_types/shared';

@Injectable({
    providedIn: 'root'
})
export class ReportsService {
    private api: string = `${environment.apiUrl}/reports`;
    private reportStatusListenerTrigger$: Subject<void> = new Subject<void>();

    constructor(private http: HttpClient) {
    }

    triggerListenForReportStatus(reportId: string): void {
        sessionStorage.setItem('awaitReport', JSON.stringify(reportId));
        this.reportStatusListenerTrigger$.next();
    }

    reportStatusListener(): Observable<void> {
        return this.reportStatusListenerTrigger$.asObservable();
    }

    checkReportStatus(id: string): Observable<GenericResponse<{ status: string }>> {
        return this.http.get<GenericResponse<{
            status: string
        }>>(`${this.api}/status/${id}/`, {responseType: 'text' as any});
    }

    generateEngagementReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/engagement-reports/`, {}, {params: this.generateHttpParams(data)});
    }

    getEngagementReports(page: number, perPage = 10, communityId?: number | string): Observable<GenericResponseWithPagination<Report>> {
        let params: HttpParams = new HttpParams();
        params = params.append('page', page);
        params = params.append('page_size', perPage);

        if (communityId && communityId != 'All') {
            params = params.append('community_id', communityId);
        }
        return this.http.get<GenericResponseWithPagination<Report>>(`${this.api}/engagement-reports/all/`, {params: params});
    }

    getCommunitiesReports(page: number, perPage = 10, communityId?: number | string): Observable<GenericResponseWithPagination<Report>> {
        let params: HttpParams = new HttpParams();
        params = params.append('page', page);
        params = params.append('page_size', perPage);

        if (communityId && communityId != 'All') {
            params = params.append('community_id', communityId);
        }
        return this.http.get<GenericResponseWithPagination<Report>>(`${this.api}/generated-reports/`, {params: params});
    }

    generatePowerBiReport(data: NewReport): Observable<GenericResponse<PowerBiToken>> {
        return this.http.post<GenericResponse<PowerBiToken>>(`${this.api}/engagement-reports/power-bi/generate-url-token/`, {}, {params: this.generateHttpParams(data)});
    }

    generateCommentsReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/comments/`, {}, {params: this.generateHttpParams(data)});
    }

    generateDailySimpleReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/daily-simple-report/`, {}, {params: this.generateHttpParams(data)});
    }

    generateMembersReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/members/`, {}, {params: this.generateHttpParams(data)});
    }

    generateMembersSegmentationReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/members-segmentation/`, {}, {params: this.generateHttpParams(data)});
    }

    generatePollVotesReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/poll-votes/`, {}, {params: this.generateHttpParams(data)});
    }

    generateRawActivityLogReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/raw-activity-log/`, {}, {params: this.generateHttpParams(data)});
    }

    generateRawPollsResponsesReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/raw-polls-responses/`, {}, {params: this.generateHttpParams(data)});
    }

    generateSuppressionReport(data: NewReport): Observable<GenericResponse<ReportStatus>> {
        return this.http.post<GenericResponse<ReportStatus>>(`${this.api}/generate/suppression/`, {}, {params: this.generateHttpParams(data)});
    }

    getPendingReports(): Observable<any> {
        return this.http.get<any>(`${this.api}/tasks/`);
    }

    private generateHttpParams(paramsObject: { [key: string]: any }): HttpParams {
        let params = new HttpParams();
        Object.keys(paramsObject).forEach(key => {
            const value = paramsObject[key];
            if (value !== null && value !== undefined) {
                params = params.append(key, value.toString());
            }
        });
        return params;
    }

}
